import { useSyncExternalStore } from 'react';
import type { CardSummary, UpdateCardInput } from '@kaba/shared';
import { idbDelete, idbEntries, idbPut } from './offlineStore';

/**
 * Offline ändern (0.44): Änderungen an Karten, die ohne Verbindung entstehen, werden hier der Reihe
 * nach abgelegt und beim Wiederverbinden übertragen – mit Versionsprüfung wie online. Nichts wird
 * still überschrieben: Ein Konflikt bleibt als „nicht übernommen“ stehen, bis die Person entscheidet.
 *
 * Offline möglich: neue Karte, Verschieben, Titel/Beschreibung/Termine/Priorität/Blockiert/Erledigt;
 * ab 0.45 auch Labels, Zuständige, Checkliste (anlegen, abhaken, löschen) und Kommentare als Text;
 * ab 0.46 eigene Felder, Archivieren und Wiederherstellen.
 */
interface Base {
  /** opId – macht die Übertragung wiederholbar (Server erkennt Doppeltes) */
  id: string;
  boardId: string;
  cardId: string;
  /** für die Liste: z. B. „Titel von „Sommerfest“ geändert“ */
  label: string;
  createdAt: number;
  status: 'pending' | 'conflict' | 'failed';
  message?: string;
}
export type OutboxOp =
  | (Base & { kind: 'planning'; method: 'PUT' | 'DELETE'; url: string; body: { opId: string; [key: string]: unknown } })
  | (Base & { kind: 'create'; columnId: string; title: string; placement: 'top' | 'bottom' })
  | (Base & { kind: 'move'; columnId: string; beforeId: string | null; afterId: string | null })
  | (Base & {
      kind: 'update';
      patch: Omit<UpdateCardInput, 'expectedVersion' | 'opId'>;
      /** Version, die die Person gesehen hat */
      baseVersion: number;
      /** direkt nach einer anderen ausstehenden Änderung derselben Karte: deren Ergebnisversion verwenden */
      chained: boolean;
    })
  /** 0.45: Labels/Zuständige als Änderung (wirkt auf den dann aktuellen Stand – keine Konflikte) */
  | (Base & { kind: 'labels'; add: string[]; remove: string[] })
  | (Base & { kind: 'assignees'; add: string[]; remove: string[] })
  | (Base & { kind: 'checkAdd'; itemId: string; text: string })
  | (Base & { kind: 'checkSet'; itemId: string; done: boolean })
  | (Base & { kind: 'checkDel'; itemId: string })
  | (Base & { kind: 'comment'; clientId: string; body: string })
  /** 0.46: eigenes Feld – nur, wenn der Wert noch dem gesehenen entspricht */
  | (Base & { kind: 'field'; fieldId: string; value: unknown; expected: unknown })
  | (Base & { kind: 'archive' })
  | (Base & { kind: 'restore' });

/** Neue Ablage (ohne Zeitpunkt und Status) – verteilend über alle Arten */
export type NewOutboxOp = OutboxOp extends infer T ? (T extends OutboxOp ? Omit<T, 'createdAt' | 'status'> & { createdAt?: number } : never) : never;

/** Ablage, wie sie aus der Oberfläche kommt (ID und Beschreibung ergänzt `sendOrQueue`) */
export type CardOpInput = NewOutboxOp extends infer T ? (T extends NewOutboxOp ? Omit<T, 'id' | 'label'> : never) : never;

export type SendResult = { ok: true; card?: CardSummary } | { ok: false; reason: 'offline' } | { ok: false; reason: 'conflict' | 'failed'; message: string; card?: CardSummary };
export type Sender = (op: OutboxOp, expectedVersion: number | undefined) => Promise<SendResult>;

export class Outbox {
  private ops: OutboxOp[] = [];
  private listeners = new Set<() => void>();
  private running: Promise<void> | null = null;
  private snapshot: OutboxOp[] = [];
  account: string | null = null;

  constructor(
    private send: Sender,
    private persist: { put: (key: string, op: OutboxOp) => Promise<void>; del: (key: string) => Promise<void> } | null = null,
    /** nach erfolgreicher Übertragung (z. B. Board neu laden) */
    private onDone: (boardIds: string[]) => void = () => undefined,
  ) {}

  private key(op: OutboxOp) {
    return `${this.account}|${String(op.createdAt).padStart(15, '0')}|${op.id}`;
  }
  private emit() {
    this.snapshot = [...this.ops];
    this.listeners.forEach((l) => l());
  }
  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };
  getSnapshot = () => this.snapshot;

  load(account: string, ops: OutboxOp[]) {
    this.account = account;
    this.ops = ops.sort((a, b) => a.createdAt - b.createdAt);
    this.emit();
  }

  /** Steht für diese Karte noch etwas aus? Dann muss jede weitere Änderung ebenfalls in die Reihe */
  hasPendingFor(cardId: string) {
    return this.ops.some((o) => o.cardId === cardId);
  }
  pendingCount() {
    return this.ops.filter((o) => o.status === 'pending').length;
  }

  async add(op: NewOutboxOp) {
    const last = this.ops.at(-1)?.createdAt ?? 0;
    const full = { ...op, createdAt: Math.max(op.createdAt ?? Date.now(), last + 1), status: 'pending' } as OutboxOp;
    this.ops.push(full);
    this.emit();
    await this.persist?.put(this.key(full), full);
  }

  async remove(id: string) {
    const op = this.ops.find((o) => o.id === id);
    if (!op) return;
    this.ops = this.ops.filter((o) => o.id !== id);
    this.emit();
    await this.persist?.del(this.key(op));
  }

  /** Ablage gezielt ändern (z. B. gesehenen Wert für eine bewusste Übernahme) */
  async amend(id: string, patch: Partial<OutboxOp>) {
    const op = this.ops.find((o) => o.id === id);
    if (op) await this.update(op, patch);
  }

  private async update(op: OutboxOp, patch: Partial<OutboxOp>) {
    Object.assign(op, patch);
    this.emit();
    await this.persist?.put(this.key(op), op);
  }

  /** Alles Ausstehende der Reihe nach übertragen; ohne Verbindung abbrechen und später erneut */
  flush(): Promise<void> {
    if (this.running) return this.running;
    this.running = this.run().finally(() => (this.running = null));
    return this.running;
  }

  private async run() {
    const blockedCards = new Set<string>();
    const touched = new Set<string>();
    for (const op of [...this.ops]) {
      if (op.status !== 'pending') {
        // Eine offene Entscheidung hält spätere Änderungen derselben Karte zurück
        blockedCards.add(op.cardId);
        continue;
      }
      if (blockedCards.has(op.cardId)) {
        await this.update(op, { status: 'conflict', message: 'Eine frühere Änderung an dieser Karte wurde nicht übernommen.' });
        continue;
      }
      const expected = op.kind === 'update' ? op.baseVersion : undefined;
      const r = await this.send(op, expected);
      if (!r.ok && r.reason === 'offline') break;
      touched.add(op.boardId);
      if (r.ok) {
        await this.remove(op.id);
        // Spätere Änderungen derselben Karte bauen auf diesem Ergebnis auf – aber nur um die eigenen
        // Versionsschritte (versionierte Felder bzw. Anlage); fremde Änderungen fallen so weiter als Konflikt auf
        const from = op.kind === 'create' ? 1 : op.kind === 'update' && touchesVersioned(op.patch) ? op.baseVersion : null;
        if (r.card && from !== null)
          for (const o of this.ops) if (o.cardId === op.cardId && o.status === 'pending' && o.kind === 'update' && o.baseVersion === from) await this.update(o, { baseVersion: r.card.version });
        // Ebenso der Stand der Beschreibung (0.48) – nur um den eigenen Schritt
        const rev = op.kind === 'update' && op.patch.description !== undefined ? op.patch.expectedDescriptionRev : undefined;
        if (r.card && rev !== undefined)
          for (const o of this.ops)
            if (o.cardId === op.cardId && o.status === 'pending' && o.kind === 'update' && o.patch.expectedDescriptionRev === rev)
              await this.update(o, { patch: { ...o.patch, expectedDescriptionRev: r.card.descriptionRev } });
      } else {
        blockedCards.add(op.cardId);
        await this.update(op, { status: r.reason, message: r.message });
      }
    }
    if (touched.size) this.onDone([...touched]);
  }

  /** Bei einem Konflikt bewusst die eigene Fassung übernehmen (mit der aktuellen Serverversion) */
  async retry(id: string, currentVersion?: number) {
    const op = this.ops.find((o) => o.id === id);
    if (!op) return;
    const patch: Partial<OutboxOp> = { status: 'pending', message: undefined };
    if (op.kind === 'update' && currentVersion !== undefined) {
      // Spätere eigene Änderungen, die auf derselben gesehenen Version beruhen, ziehen mit
      for (const o of this.ops) if (o !== op && o.cardId === op.cardId && o.kind === 'update' && o.baseVersion === op.baseVersion) await this.update(o, { baseVersion: currentVersion });
      // Bewusst übernehmen: auch gemeinsam Geschriebenes in der Beschreibung wird ersetzt
      const { expectedDescriptionRev: _r, ...rest } = op.patch;
      Object.assign(patch, { baseVersion: currentVersion, chained: false, patch: rest });
    }
    await this.update(op, patch);
    // Spätere, nur wegen dieser Änderung zurückgehaltene Änderungen derselben Karte wieder freigeben
    for (const o of this.ops) if (o !== op && o.cardId === op.cardId && o.status === 'conflict' && o.message?.startsWith('Eine frühere')) await this.update(o, { status: 'pending', message: undefined, ...(o.kind === 'update' ? { chained: true } : {}) });
    await this.flush();
  }
}

/** Felder mit Versionsprüfung (wie VERSIONED_FIELDS auf dem Server) */
const VERSIONED = ['title', 'description', 'dueAt', 'startAt', 'completed'] as const;
export const touchesVersioned = (p: object) => VERSIONED.some((f) => f in p);

/* ---------- Verdrahtung für die App ---------- */

let instance: Outbox | null = null;
export function outbox(): Outbox {
  if (!instance) throw new Error('Outbox nicht eingerichtet');
  return instance;
}
export function setupOutbox(send: Sender, onDone: (boardIds: string[]) => void) {
  instance = new Outbox(send, { put: (k, op) => idbPut('outbox', k, op), del: (k) => idbDelete('outbox', k) }, onDone);
  return instance;
}
export async function loadOutbox(account: string) {
  const entries = await idbEntries<OutboxOp>('outbox', `${account}|`);
  outbox().load(
    account,
    entries.map(([, v]) => v),
  );
}

const EMPTY: OutboxOp[] = [];
export function useOutbox(): OutboxOp[] {
  return useSyncExternalStore(
    (cb) => (instance ? instance.subscribe(cb) : () => undefined),
    () => instance?.getSnapshot() ?? EMPTY,
  );
}
